/**
 * What the platform's fused-tracks feed is showing right now (ENG-342): drives
 * the "Fused Tracks" card in the Surveillance sidebar. Derived from the targets
 * on screen, so there is no second store to keep in step.
 */
import { FUSED_INPUT_FEEDS } from '@/lib/mqtt/legacy/fusedTracks';
import type { Target } from '@/lib/mqtt/schemas/targets';

/** `selectedNetwork` value for the Fused Tracks source in the Surveillance list. */
export const FUSED_TRACKS_ID = 'fused_tracks';

/** Short label per input feed, for the card and the "seen by" breakdown. */
export const FEED_LABEL: Record<string, string> = {
  live_ads: 'ADS',
  live_mlat: 'MLAT',
  live_community: 'COM',
};

/** A fused track older than this has stopped being refreshed. The feed publishes
 *  at 1 Hz and targets are re-emitted every second, so this allows a few misses. */
export const FUSED_SUMMARY_FRESH_MS = 5_000;

export interface FusedSummary {
  /** The fused feed is publishing and its tracks are on the map. */
  live: boolean;
  tracks: number;
  coasting: number;
  /** Aircraft each raw feed is currently reporting among the fused tracks. */
  perFeed: Record<string, number>;
  /** Tracks grouped by exactly which feeds report them, e.g. `ADS + MLAT`;
   *  `none` when no raw report is fresh. */
  seenBy: Record<string, number>;
}

export function fusedSummary(byId: Record<string, Target>, nowMs: number): FusedSummary {
  const perFeed: Record<string, number> = Object.fromEntries(FUSED_INPUT_FEEDS.map((f) => [f, 0]));
  let tracks = 0;
  let coasting = 0;
  const seenBy: Record<string, number> = {};
  for (const t of Object.values(byId)) {
    if (t.sources === undefined) continue;
    if (nowMs - Date.parse(t.received_at) > FUSED_SUMMARY_FRESH_MS) continue;
    tracks += 1;
    if (t.coasting) coasting += 1;
    const feeds = [...new Set(t.sources.map((s) => s.service_id))];
    for (const feed of feeds) perFeed[feed] = (perFeed[feed] ?? 0) + 1;
    const key =
      feeds
        .map((f) => FEED_LABEL[f] ?? f)
        .sort()
        .join(' + ') || 'none';
    seenBy[key] = (seenBy[key] ?? 0) + 1;
  }
  return { live: tracks > 0, tracks, coasting, perFeed, seenBy };
}

/** Straight-line distance in metres between two close positions. Good enough for
 *  the few-hundred-metre to few-km gaps between a feed's report and the fused
 *  position; not for anything long-range. */
export function offsetMetres(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const mPerDegLat = 111_320;
  const dLat = (a.lat - b.lat) * mPerDegLat;
  const dLng = (a.lng - b.lng) * mPerDegLat * Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}
